import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { CATALOG_TYPES } from "@/constants";

function CatalogPanel({ type, label }) {
  const qc = useQueryClient();
  const { data: items = [], refetch } = useQuery({
    queryKey: ["catalogs", type],
    queryFn: async () => (await api.get("/catalogs", { params: { type } })).data,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");

  const openCreate = () => { setEditing(null); setName(""); setDialogOpen(true); };
  const openEdit = (it) => { setEditing(it); setName(it.name); setDialogOpen(true); };

  const save = async () => {
    if (!name.trim()) return toast.error("Nombre requerido");
    try {
      if (editing) {
        await api.patch(`/catalogs/${editing.id}`, { name: name.trim() });
        toast.success("Actualizado");
      } else {
        await api.post("/catalogs", { type, name: name.trim() });
        toast.success("Agregado");
      }
      setDialogOpen(false);
      refetch();
      qc.invalidateQueries({ queryKey: ["catalogs-all"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/catalogs/${id}`);
      toast.success("Eliminado");
      refetch();
      qc.invalidateQueries({ queryKey: ["catalogs-all"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold tracking-tight">{label}</h3>
            <p className="text-xs text-muted-foreground">{items.length} elemento{items.length !== 1 ? "s" : ""}</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={openCreate} data-testid={`catalog-${type}-create-button`}><Plus className="h-4 w-4 mr-1" /> Agregar</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editing ? `Editar ${label}` : `Nuevo ${label}`}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Nombre</Label>
                  <Input data-testid="catalog-name-input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                <Button onClick={save} data-testid="catalog-save-button">Guardar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <div className="rounded-md border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 px-3">Nombre</th>
                <th className="py-2 px-3 w-32">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-border/60 hover:bg-muted/30">
                  <td className="py-2 px-3">{it.name}</td>
                  <td className="py-2 px-3">
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(it)} data-testid={`catalog-edit-${it.id}`}><Edit className="h-3.5 w-3.5" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" data-testid={`catalog-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar "{it.name}"?</AlertDialogTitle>
                            <AlertDialogDescription>Las OTs existentes que usen este valor no se afectan.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(it.id)} data-testid="catalog-delete-confirm-button">Eliminar</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={2} className="py-8 text-center text-muted-foreground text-sm">Sin elementos. Agrega el primero.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function CatalogsPage() {
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Catálogos</h1>
        <p className="text-sm text-muted-foreground">Gestiona los valores de las listas desplegables de OT.</p>
      </div>
      <Tabs defaultValue="cliente">
        <TabsList>
          {CATALOG_TYPES.map((c) => (
            <TabsTrigger key={c.key} value={c.key} data-testid={`catalog-tab-${c.key}`}>{c.label}</TabsTrigger>
          ))}
        </TabsList>
        {CATALOG_TYPES.map((c) => (
          <TabsContent key={c.key} value={c.key} className="mt-3">
            <CatalogPanel type={c.key} label={c.label} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
